import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
// useAgent.tsx -- thin compatibility shim over the chatStore.
//
// All chat state moved to client/src/state/chatStore.ts -- this
// module's only remaining job is to:
//   1. Bind project context (projectId/project/role) into the store
//      on mount, since the store is otherwise fully decoupled from
//      React's tree.
//   2. Expose useProjectContext() for components that just want
//      project metadata without subscribing to chat windows.
//
// Side-effect handlers are registered directly on the store via
// useChatStore.getState().setSideEffectHandlers(...). The store
// invokes them when it dispatches tool side-effects from the SSE
// reducer.
import { useEffect } from 'react';
import { useChatStore } from '../state/chatStore.js';
/**
 * Thin provider that pushes project context into the store on mount
 * and on prop changes. Components inside this tree can use
 * useProjectContext() OR pull directly from the store via
 * useChatStore selectors.
 */
export function AgentProvider({ projectId, project, role, children, }) {
    useEffect(() => {
        const store = useChatStore.getState();
        store.setProjectContext({ projectId, project, role });
        // Restore persisted windows from a previous session. hydrate is
        // idempotent so a re-fire here (StrictMode, prop change) is safe;
        // it just no-ops when windows already exist.
        store.hydrateFromStorage();
    }, [projectId, project, role]);
    return _jsx(_Fragment, { children: children });
}
/**
 * Project metadata accessor. Returns null fields until the provider
 * has run its mount effect; in practice consumers only render inside
 * the provider so the values are always populated.
 */
export function useProjectContext() {
    return {
        projectId: useChatStore((s) => s.projectId),
        project: useChatStore((s) => s.project),
        role: useChatStore((s) => s.role),
    };
}
export { useChatStore, useChatWindow, useChatWindowIds, useMinimizedIndex, useProjectId, } from '../state/chatStore.js';
